import {CreateNewDocProps, CreateQuizLinkProps} from 'app/general/types';
import {sweetAPI} from 'app/store/sweet-api';
import {
  providesTagsByURL,
  showLoadingBar,
  showMessages,
} from 'app/store/sweet-api/utils';
import {
  QUIZ_API_URL,
  QUIZ_API_URL_COPY,
  QUIZ_ATTEMPT_ANSWER_API_URL,
  QUIZ_ATTEMPT_API_URL,
  QUIZ_COMPLETE_ATTEMPT_API_URL,
  QUIZ_LINK_API_URL,
  QUIZ_VERSION_API_URL,
  QUIZ_VERSION_APPROVE_API_URL,
} from 'app/urls';
import {DEFAULT_PAGE_SIZE} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';
import {DOCUMENT_TAG, QUIZ_TAGS, SEARCH_TAG} from 'app/utils/rtk-tags';
import {decrypt} from 'app/utils/strings';

import type {
  QuizzesFetchParamsType,
  QuizzesLocationParamsType,
} from 'app/quizzes/types';
import type {
  AnswerChoiceType,
  AnswerFreeformType,
  AttemptSerializedType,
  ListResponseType,
  QuizSerializedType,
  QuizVersionSerializedType,
  RawQuizSerializedType,
  RawQuizVersionSerializedType,
} from 'sweet-types';

export const extendedAPI = sweetAPI
  .enhanceEndpoints({
    addTagTypes: [
      QUIZ_TAGS.TAG,
      QUIZ_TAGS.VERSION,
      QUIZ_TAGS.ATTEMPT,
      DOCUMENT_TAG,
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      quizzes: build.query<
        ListResponseType<QuizSerializedType>,
        QuizzesFetchParamsType
      >({
        query: (params) => ({
          url: QUIZ_API_URL,
          params,
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to load the quizzes.'),
          })(args, api);
        },
        providesTags: providesTagsByURL(QUIZ_TAGS.TAG),
      }),
      quiz: build.query<QuizSerializedType, string>({
        query: (id) => ({
          url: `${QUIZ_API_URL}${id}/`,
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to load the quiz.'),
          })(args, api);
        },
        providesTags: (response) =>
          response != null
            ? [
                {type: QUIZ_TAGS.TAG, id: response.url},
                {type: QUIZ_TAGS.TAG, id: 'list'},
              ]
            : [],
        transformResponse: async (response: RawQuizSerializedType) => {
          const content = await decrypt(
            response.encrypted_content,
            response.hashid
          );
          return {
            ...response,
            content: JSON.parse(content),
            encrypted_content: undefined,
          };
        },
      }),
      deleteQuiz: build.mutation<unknown, string>({
        query: (url) => ({
          url,
          method: 'DELETE',
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Could not delete quiz.'),
            onSuccess: () => gt('Quiz deleted'),
          })(args, api);
        },
        invalidatesTags: () => [{type: QUIZ_TAGS.TAG, id: 'list'}],
      }),
      createQuiz: build.mutation<QuizSerializedType, CreateNewDocProps>({
        query: (data) => ({
          url: QUIZ_API_URL,
          method: 'POST',
          body: data,
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: {
              fallback: () => gt('Could not create quiz.'),
              byStatus: {
                403: (response) =>
                  response && Object.values(response).flat().join(', '),
              },
            },
          })(args, api);
        },
        invalidatesTags: () => [
          {type: QUIZ_TAGS.TAG, id: 'list'},
          {type: DOCUMENT_TAG, id: 'list'},
        ],
      }),
      quizVersions: build.query<
        ListResponseType<QuizVersionSerializedType>,
        {
          quizId?: string | number;
          page?: string | number;
          page_size?: string;
        }
      >({
        query: ({quizId, page = 1, page_size = DEFAULT_PAGE_SIZE}) => ({
          url: QUIZ_VERSION_API_URL,
          params: {version_object_id: quizId, page, page_size},
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to load the quiz versions.'),
          })(args, api);
        },
        providesTags: providesTagsByURL(QUIZ_TAGS.VERSION),
      }),
      quizVersionDetail: build.query<
        QuizVersionSerializedType,
        {versionId: string}
      >({
        query: ({versionId}) => ({
          url: `${QUIZ_VERSION_API_URL}${versionId}/`,
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to load the quiz version.'),
          })(args, api);
        },
        providesTags: (response) =>
          response != null
            ? [
                {type: QUIZ_TAGS.VERSION, id: response.url},
                {type: QUIZ_TAGS.VERSION, id: 'list'},
              ]
            : [],
        transformResponse: async (response: RawQuizVersionSerializedType) => {
          const content = await decrypt(
            response.encrypted_content,
            response.hashid
          );
          return {
            ...response,
            content: JSON.parse(content),
            encrypted_content: undefined,
          };
        },
      }),
      updateQuizVersion: build.mutation<
        QuizVersionSerializedType,
        {id: string; body: Partial<QuizVersionSerializedType>}
      >({
        query: ({id, body}) => ({
          url: `${QUIZ_VERSION_API_URL}${id}/`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: (response) =>
          response != null
            ? [
                {type: QUIZ_TAGS.VERSION, id: 'list'},
                {type: QUIZ_TAGS.VERSION, id: response.url},
              ]
            : [],
      }),
      approveQuizVersion: build.mutation<
        unknown,
        {
          versionURL: string;
          documentId: number;
        }
      >({
        query: ({versionURL}) => ({
          url: QUIZ_VERSION_APPROVE_API_URL,
          method: 'POST',
          body: {url: versionURL},
        }),
        onQueryStarted: (args, api) => {
          showMessages({
            onSuccess: () => gt('This version is now approved and live.'),
            onError: () => gt('Could not approve that version.'),
          })(args, api);
        },
        invalidatesTags: (response) =>
          response != null
            ? [
                {type: QUIZ_TAGS.VERSION, id: 'list'},
                {type: QUIZ_TAGS.TAG, id: 'list'},
              ]
            : [],
      }),
      addQuizLink: build.mutation<unknown, CreateQuizLinkProps>({
        query: (data) => ({
          url: QUIZ_LINK_API_URL,
          method: 'POST',
          body: data,
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Could not create quiz link.'),
          })(args, api);
        },
        invalidatesTags: () => [
          {type: QUIZ_TAGS.TAG, id: 'list'},
          {type: DOCUMENT_TAG, id: 'list'},
          {type: SEARCH_TAG, id: 'list'},
        ],
      }),
      removeQuizLink: build.mutation<unknown, {url: string}>({
        query: ({url}) => ({
          url,
          method: 'DELETE',
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Could not delete quiz link.'),
          })(args, api);
        },
        invalidatesTags: () => [
          {type: QUIZ_TAGS.TAG, id: 'list'},
          {type: DOCUMENT_TAG, id: 'list'},
          {type: SEARCH_TAG, id: 'list'},
        ],
      }),
      attempt: build.query<AttemptSerializedType, string>({
        query: (id) => ({
          url: `${QUIZ_ATTEMPT_API_URL}${id}/`,
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: {
              fallback: (response) =>
                response?.message ?? gt('Unable to load your attempt.'),
              byStatus: {},
              silenceByStatus: {404: true},
            },
          })(args, api);
        },
        providesTags: (response) =>
          response != null
            ? [
                {type: QUIZ_TAGS.ATTEMPT, id: response.url},
                {type: QUIZ_TAGS.ATTEMPT, id: 'list'},
              ]
            : [],
      }),
      attempts: build.query<
        ListResponseType<AttemptSerializedType>,
        {
          quiz?: string;
          page?: string | number;
          page_size?: string;
        }
      >({
        query: ({page = 1, page_size = DEFAULT_PAGE_SIZE, quiz}) => ({
          url: QUIZ_ATTEMPT_API_URL,
          params: {page, page_size, quiz},
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to load attempts.'),
          })(args, api);
        },
        providesTags: (response) =>
          response != null ? [{type: QUIZ_TAGS.ATTEMPT, id: 'list'}] : [],
      }),
      attemptQuiz: build.mutation<AttemptSerializedType, {quiz: string}>({
        query: ({quiz}) => ({
          url: QUIZ_ATTEMPT_API_URL,
          method: 'POST',
          body: {quiz},
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: {
              fallback: () => gt('Could not begin attempt for this quiz.'),
              byStatus: {
                400: (response) =>
                  response && Object.values(response).flat().join(', '),
              },
              silenceByStatus: {404: true},
            },
          })(args, api);
        },
        invalidatesTags: () => [{type: QUIZ_TAGS.ATTEMPT, id: 'list'}],
      }),
      duplicateQuiz: build.mutation<
        QuizSerializedType,
        {name: string; url: string; teams?: Array<string>}
      >({
        query: (body) => ({
          url: QUIZ_API_URL_COPY,
          method: 'POST',
          body,
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to duplicate'),
          })(args, api);
        },
        invalidatesTags: () => [{type: QUIZ_TAGS.TAG, id: 'list'}],
      }),
      deleteAttempt: build.mutation<unknown, string>({
        query: (url) => ({
          url,
          method: 'DELETE',
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Could not delete attempt.'),
            onSuccess: () => gt('Attempt deleted'),
          })(args, api);
        },
        invalidatesTags: () => [{type: QUIZ_TAGS.ATTEMPT, id: 'list'}],
      }),
      answerQuestion: build.mutation<
        AttemptSerializedType,
        Omit<AnswerChoiceType | AnswerFreeformType, 'type'>
      >({
        query: ({attempt_id, question_id, ...other}) => ({
          url: `${QUIZ_ATTEMPT_ANSWER_API_URL}${attempt_id}/question/${question_id}/`,
          method: 'PATCH',
          body: {
            ...other,
          },
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Could not answer question.'),
          })(args, api);
        },
        invalidatesTags: () => [{type: QUIZ_TAGS.ATTEMPT, id: 'list'}],
      }),
      completeAttempt: build.mutation<
        AttemptSerializedType,
        {
          attempt: AttemptSerializedType;
        }
      >({
        query: ({attempt}) => ({
          url: `${QUIZ_COMPLETE_ATTEMPT_API_URL}${attempt.id}/`,
          method: 'PATCH',
          body: {
            answer: attempt.answers,
          },
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: {
              fallback: () => gt('Could not submit attempt.'),
              byStatus: {
                400: (response) =>
                  response && Object.values(response).flat().join(', '),
              },
            },
          })(args, api);
        },
        invalidatesTags: () => [{type: QUIZ_TAGS.ATTEMPT, id: 'list'}],
      }),
    }),
  });

export const {
  useQuizzesQuery: useQuizzes,
  useQuizQuery: useQuiz,
  useQuizVersionsQuery: useQuizVersions,
  useQuizVersionDetailQuery: useQuizVersionDetail,
  useUpdateQuizVersionMutation: useUpdateQuizVersion,
  useDeleteQuizMutation: useDeleteQuiz,
  useApproveQuizVersionMutation: useApproveQuizVersion,
  useCreateQuizMutation: useCreateQuiz,
  useAddQuizLinkMutation: useAddQuizLink,
  useRemoveQuizLinkMutation: useRemoveQuizLink,
  useAttemptQuizMutation: useAttemptQuiz,
  useDuplicateQuizMutation: useDuplicateQuiz,
  useAttemptQuery: useAttempt,
  useAttemptsQuery: useAttempts,
  useDeleteAttemptMutation: useDeleteAttempt,
  useAnswerQuestionMutation: useAnswerQuestion,
  useCompleteAttemptMutation: useCompleteAttempt,
} = extendedAPI;

export const convertLocationParamsToFetchParams = (
  query: QuizzesLocationParamsType
): QuizzesFetchParamsType => {
  return {
    ...query,
    page: query.page != null ? query.page : '1',
    page_size:
      query.page_size != null ? query.page_size : DEFAULT_PAGE_SIZE.toString(),
  };
};

// read cached data - to be used by useSelector / class components
export const quizVersionsSelector = ({
  quizId,
  page,
  page_size,
}: {
  quizId?: string | number;
  page?: string | number;
  page_size?: string;
}) =>
  extendedAPI.endpoints.quizVersions.select({
    quizId,
    page,
    page_size,
  });
